import { Layer, TableToolbar, TableToolbarContent } from "@carbon/react";
import {
  MultiSelectAppliedFilter,
  MultiSelectAppliedFilterSkeleton,
  SingleListAppliedFilterSkeleton,
  SingleSelectAppliedFilter,
} from "@flaminem/ui-components";
import React, { useEffect, useState } from "react";

import { AppliedFilters } from "src/components/Filters/AppliedFilters.tsx";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { useGetCaseTypesSchemasQuery } from "src/graphql/anti-fraud/queries/caseTypes/CaseTypesSchemasQuery";
import { useGetRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { useGetUsersQuery } from "src/graphql/anti-fraud/queries/users/GetUsersQuery";
import { useAlertValidationDecisions } from "src/hooks/alerts/useAlertValidationDecisions";
import { debounceSearchTime } from "src/hooks/useSearch.ts";
import { useT } from "src/internationalization";
import { AlertsFilter } from "src/pages/Alerts/AlertsFilter.tsx";
import { AlertValidationDecision } from "src/types/alert";
import { debounce } from "src/utils/functions.utils.ts";

type AlertsToolbarProps = {
  searchTerm: string;
  handleOnSearchTermChange: (value: string) => void;
  ruleIds: string[];
  caseTypeCodes: string[];
  onlyUserAlerts: boolean;
  assignedTo: string;
  decisions: AlertValidationDecision[];
  displayDecisionFilter: boolean;
  placeholder?: string;
  handleOnFiltersChange: (filters: {
    ruleIds: string[];
    onlyUserAlerts: boolean;
    decisions: AlertValidationDecision[];
    caseTypeCodes: string[];
    assignedTo: string;
  }) => void;
};

const AlertsToolbar: React.FC<AlertsToolbarProps> = ({
  searchTerm,
  handleOnSearchTermChange,
  ruleIds,
  handleOnFiltersChange,
  onlyUserAlerts,
  assignedTo,
  displayDecisionFilter,
  decisions,
  caseTypeCodes,
  placeholder,
}) => {
  const { data: users, loading: usersLoading, error: usersError } = useGetUsersQuery();
  const selectedUser = users.find((user) => user.id === assignedTo);

  const { data: rules, loading: rulesLoading, error: rulesError } = useGetRulesQuery();
  const selectedRuleItems = rules.filter((rule) => ruleIds.includes(rule.id));

  const {
    data: caseTypes,
    loading: caseTypesLoading,
    error: caseTypesError,
  } = useGetCaseTypesSchemasQuery();

  const selectedCaseTypeItems = caseTypes?.filter((caseType) =>
    caseTypeCodes.includes(caseType.code)
  );

  const decisionsData = useAlertValidationDecisions();
  const selectedDecisionItems = decisionsData.filter((decision) => decisions.includes(decision.id));

  const t = useT();
  const [currentSearchTerm, setCurrentSearchTerm] = useState(searchTerm);

  useEffect(() => {
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  const debouncedSearch = React.useMemo(() => {
    return debounce(handleOnSearchTermChange, debounceSearchTime);
    // We don't want to re-create the debounced function on every render, it makes unnecessary calls to the API
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearchTerm = (value: string) => {
    setCurrentSearchTerm(value);
    debouncedSearch(value);
  };

  const shouldDisplaySelectedFilters =
    (selectedRuleItems && selectedRuleItems.length > 0) ||
    (selectedDecisionItems && selectedDecisionItems.length > 0) ||
    onlyUserAlerts ||
    !!assignedTo ||
    (selectedCaseTypeItems && selectedCaseTypeItems.length > 0) ||
    decisions.length > 0;

  return (
    <>
      <TableToolbar aria-label="data table toolbar">
        <TableToolbarContent className="border-b border-solid border-borderSubtle01">
          <TableToolbarSearch
            placeholder={placeholder || t("page.alerts.table.search.placeholder")}
            onSearchChange={setSearchTerm}
            search={currentSearchTerm}
          />
          <AlertsFilter
            displayDecisionFilter={displayDecisionFilter}
            values={{ ruleIds, onlyUserAlerts, decisions, caseTypeCodes, assignedTo }}
            onApplyFilter={handleOnFiltersChange}
            onResetFilter={() =>
              handleOnFiltersChange({
                ruleIds: [],
                onlyUserAlerts: false,
                decisions: [],
                caseTypeCodes: [],
                assignedTo: "",
              })
            }
            caseTypes={caseTypes}
            rules={rules}
            users={users}
            loading={rulesLoading || caseTypesLoading}
            error={rulesError || caseTypesError || usersError}
          />
        </TableToolbarContent>
      </TableToolbar>
      {shouldDisplaySelectedFilters && (
        <div className="flex w-full bg-layer">
          <Layer className={"p-5"}>
            <AppliedFilters
              onClear={() =>
                handleOnFiltersChange({
                  ruleIds: [],
                  onlyUserAlerts: false,
                  decisions: [],
                  caseTypeCodes: [],
                  assignedTo: "",
                })
              }
            >
              {rulesLoading || caseTypesLoading || usersLoading ? (
                <>
                  <SingleListAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.applied.only.user.filter.title")}
                  />
                  <SingleListAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.applied.assigned.user.title")}
                  />
                  <MultiSelectAppliedFilterSkeleton title={t("rules.title")} />
                  <MultiSelectAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.form.decisions.title")}
                  />
                  <MultiSelectAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.form.case.type.title")}
                  />
                </>
              ) : (
                <>
                  {onlyUserAlerts && (
                    <SingleSelectAppliedFilter
                      item={{
                        label: t("page.alerts.table.filters.applied.only.user.filter.value"),
                        value: t("page.alerts.table.filters.applied.only.user.filter.value"),
                      }}
                      title={t("page.alerts.table.filters.applied.only.user.filter.title")}
                      onClear={() =>
                        handleOnFiltersChange({
                          ruleIds: ruleIds,
                          onlyUserAlerts: false,
                          decisions,
                          caseTypeCodes: caseTypeCodes,
                          assignedTo,
                        })
                      }
                    />
                  )}
                  {selectedUser && (
                    <SingleSelectAppliedFilter
                      item={{
                        label: selectedUser.name,
                        value: selectedUser.id,
                      }}
                      title={t("page.alerts.table.filters.applied.assigned.user.title")}
                      onClear={() =>
                        handleOnFiltersChange({
                          ruleIds: ruleIds,
                          onlyUserAlerts,
                          decisions,
                          caseTypeCodes: caseTypeCodes,
                          assignedTo: "",
                        })
                      }
                    />
                  )}
                  {selectedRuleItems && selectedRuleItems.length > 0 && (
                    <MultiSelectAppliedFilter
                      items={selectedRuleItems.map((item) => ({
                        label: item.name,
                        value: item.id,
                      }))}
                      title={t("rules.title")}
                      onChange={(values) =>
                        handleOnFiltersChange({
                          ruleIds: values,
                          onlyUserAlerts: onlyUserAlerts,
                          decisions,
                          caseTypeCodes: caseTypeCodes,
                          assignedTo,
                        })
                      }
                    />
                  )}
                  {selectedCaseTypeItems && selectedCaseTypeItems.length > 0 && (
                    <MultiSelectAppliedFilter
                      items={selectedCaseTypeItems.map((item) => ({
                        label: item.name,
                        value: item.code,
                      }))}
                      title={t("page.alerts.table.filters.case.type.filter.title")}
                      onChange={(values) =>
                        handleOnFiltersChange({
                          ruleIds: ruleIds,
                          onlyUserAlerts: onlyUserAlerts,
                          decisions,
                          caseTypeCodes: values,
                          assignedTo,
                        })
                      }
                    />
                  )}
                  {decisions.length > 0 && (
                    <MultiSelectAppliedFilter
                      items={selectedDecisionItems.map((item) => ({
                        label: item.title,
                        value: item.id,
                      }))}
                      title={t("page.alerts.table.filters.form.decisions.title")}
                      onChange={(values) =>
                        handleOnFiltersChange({
                          ruleIds: ruleIds,
                          onlyUserAlerts: onlyUserAlerts,
                          decisions: values as AlertValidationDecision[],
                          caseTypeCodes: caseTypeCodes,
                          assignedTo,
                        })
                      }
                    />
                  )}
                </>
              )}
            </AppliedFilters>
          </Layer>
        </div>
      )}
    </>
  );
};

export default AlertsToolbar;
